/**
 * AR/AP aging. Mirrors the SQL view public.v_ar_ap_aging:
 * basis = invoice age in days (as-of date − invoice date);
 * buckets 0–30 · 31–60 · 61–90 · 90+; overdue = due date before as-of date.
 */
import { daysBetween, type DateInput } from './format';
import { D, sum, type Dec, type DecimalInput } from './money';

export type AgingBucket = 'b0_30' | 'b31_60' | 'b61_90' | 'b90_plus';
export const AGING_BUCKETS: AgingBucket[] = ['b0_30', 'b31_60', 'b61_90', 'b90_plus'];

export function agingBucket(invoiceDate: DateInput, asOf: DateInput = new Date()): AgingBucket {
  const age = daysBetween(invoiceDate, asOf);
  if (age <= 30) return 'b0_30';
  if (age <= 60) return 'b31_60';
  if (age <= 90) return 'b61_90';
  return 'b90_plus';
}

export interface OpenItem {
  date: DateInput;
  dueDate?: DateInput | null;
  remaining: DecimalInput;
}

export interface AgingSummary extends Record<AgingBucket, Dec> {
  overdue: Dec;
  total: Dec;
}

export function agingSummary(items: OpenItem[], asOf: DateInput = new Date()): AgingSummary {
  const out: AgingSummary = {
    b0_30: D(0),
    b31_60: D(0),
    b61_90: D(0),
    b90_plus: D(0),
    overdue: D(0),
    total: D(0),
  };
  for (const it of items) {
    const r = D(it.remaining);
    if (r.lessThanOrEqualTo(0)) continue;
    const b = agingBucket(it.date, asOf);
    out[b] = out[b].plus(r);
    if (it.dueDate && daysUntilDue(it.dueDate, asOf) < 0) out.overdue = out.overdue.plus(r);
  }
  out.total = sum(AGING_BUCKETS.map((b) => out[b]));
  return out;
}

/** Positive = days left, 0 = due today, negative = days overdue. */
export function daysUntilDue(dueDate: DateInput, asOf: DateInput = new Date()): number {
  return daysBetween(asOf, dueDate);
}

/** Share of each bucket in % (for the aging bar), rounded to 1 decimal. */
export function agingShares(s: AgingSummary): Record<AgingBucket, number> {
  const total = s.total;
  const res = {} as Record<AgingBucket, number>;
  for (const b of AGING_BUCKETS) {
    res[b] = total.isZero() ? 0 : s[b].dividedBy(total).times(100).toDecimalPlaces(1).toNumber();
  }
  return res;
}
